import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge class names
 * @param {...string} inputs - The class names to merge
 * @returns {string} The merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Debounce
 * @param {Function} func - The function to debounce
 * @param {number} wait - The wait time in milliseconds
 * @returns {Function} The debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle
 * @param {Function} func - The function to throttle
 * @param {number} limit - The limit in milliseconds
 * @returns {Function} The throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Convert SVG to PNG
 * @param {string} svgString - The SVG string
 * @param {number} width - The width
 * @param {number} height - The height
 * @returns {Promise<string>} The PNG data URL
 */
export async function svgToPng(svgString, width = 100, height = 100) {
  return new Promise((resolve, reject) => {
    // Create an image element
    const img = new Image();
    // Set the SVG string as the source of the image
    const svgBlob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      // Create a canvas element
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      // Draw the SVG image onto the canvas
      ctx.drawImage(img, 0, 0, width, height);
      // Convert the canvas to a data URL (PNG format)
      const pngDataUrl = canvas.toDataURL("image/png");
      // Clean up
      URL.revokeObjectURL(url);
      resolve(pngDataUrl);
    };
    img.onerror = (error) => {
      // Clean up
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load SVG image"));
    };
    // Start loading the image
    img.src = url;
  });
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID
 * @param {string} str - The string to check
 * @returns {boolean} True if the string is a valid UUID, false otherwise
 */
export function checkIsUUID(str) {
  return UUID_REGEX.test(str);
}
/**
 * Ensure UUID
 * @param {string} value - The value to ensure
 * @returns {string} The UUID
 */
export const ensureUUID = (value) => {
  if (!value) return null;
  // Remove any non-alphanumeric characters and dashes
  const cleaned = value.toString().replace(/[^a-fA-F0-9-]/g, "");
  // Check if it matches UUID format
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      cleaned
    )
  ) {
    return cleaned;
  }
  return null;
};

// Utility function to check if string is a valid UUID
export const isValidUUID = (id) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

// Utility function to check if string is a valid OpenAI assistant ID
export const isValidOpenAIId = (id) => {
  return typeof id === "string" && id.startsWith("asst_");
};

/**
 * Extracts initials from a name string
 * @param {string} name - The name to extract initials from
 * @param {number} [limit=2] - Maximum number of initials to return
 * @returns {string} The extracted initials
 */
export function getInitials(name, limit = 2) {
  if (!name) return "";

  return name
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, limit)
    .join("");
}

export function formatFileSize(bytes) {
  if (!bytes) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function formatDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}
